import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheckIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email: email.trim(), password });
      login(data.token, data.user);
      toast.success("Admin access granted");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid admin email or password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6"
      style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,215,0,0.06), transparent 40%)" }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md border border-border bg-card p-8">
        <div className="flex items-center gap-2 text-accent mb-6">
          <ShieldCheckIcon weight="fill" size={22} />
          <span className="font-mono text-xs uppercase tracking-[0.25em]">Super admin</span>
        </div>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <h1 className="font-heading font-black text-4xl tracking-tighter leading-none">Admin sign-in</h1>
            <p className="text-white/50 mt-3">Restricted. Email + password.</p>
          </div>
          <div className="space-y-2">
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Email</label>
            <Input data-testid="admin-email-input" type="email" required autoFocus placeholder="admin@dcode.co"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-14 bg-secondary/40 border-border font-mono focus-visible:ring-accent" />
          </div>
          <div className="space-y-2">
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Password</label>
            <Input data-testid="admin-password-input" type="password" required placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="h-14 bg-secondary/40 border-border font-mono focus-visible:ring-accent" />
          </div>
          <Button data-testid="admin-login-btn" type="submit" disabled={busy || !email.trim() || !password}
            className="w-full h-14 bg-accent text-accent-foreground font-bold hover:bg-accent/90 transition-colors">
            {busy ? "SIGNING IN…" : "SIGN IN"}
          </Button>
          <a href="/login" className="block text-center font-mono text-[11px] text-white/40 hover:text-white transition-colors">
            Not an admin? Customer sign-in →
          </a>
        </form>
      </motion.div>
    </div>
  );
}
